import React, { useState, useRef, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Alert,
} from "react-bootstrap";
import {
  FaSearch,
  FaMapPin,
  FaTimes,
  FaArrowRight,
  FaArrowDown,
  FaSearchPlus,
  FaSearchMinus,
  FaRedo,
  FaTrashAlt,
  FaRoute,
} from "react-icons/fa";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const ELEMENT_TYPE = "ROUTE_ELEMENT";

// Sample route stops data (later this will come from API)
const ROUTE_STOPS = [
  { id: 1, name: "Main Gate", code: "MG001" },
  { id: 2, name: "Library Junction", code: "LJ002" },
  { id: 3, name: "Academic Block A", code: "ABA003" },
  { id: 4, name: "Sports Complex", code: "SC004" },
  { id: 5, name: "Hostel Block 1", code: "HB1005" },
  { id: 6, name: "Hostel Block 2", code: "HB2006" },
  { id: 7, name: "Cafeteria", code: "CAF007" },
  { id: 8, name: "Admin Building", code: "AB008" },
  { id: 9, name: "Parking Area", code: "PA009" },
  { id: 10, name: "Medical Center", code: "MC010" },
];

// Individual draggable element component
const RouteElement = ({
  id,
  number,
  position,
  onDelete,
  onConnect,
  isConnecting,
  stopData,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [{ isDragging }, drag] = useDrag({
    type: ELEMENT_TYPE,
    item: { id, number, fromSidebar: false },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Handle connection points
  const connectionPoints = [
    {
      id: "top",
      style: { top: "-12px", left: "50%", transform: "translateX(-50%)" },
    },
    {
      id: "right",
      style: { top: "50%", right: "-12px", transform: "translateY(-50%)" },
    },
    {
      id: "bottom",
      style: { bottom: "-12px", left: "50%", transform: "translateX(-50%)" },
    },
    {
      id: "left",
      style: { top: "50%", left: "-12px", transform: "translateY(-50%)" },
    },
  ];

  const handleConnectionClick = (e, pointId) => {
    e.stopPropagation();
    e.preventDefault();
    onConnect(id, pointId);
  };

  return (
    <div
      ref={drag}
      className={`position-absolute user-select-none ${
        isDragging ? "opacity-75" : ""
      } ${isConnecting ? "shadow-lg" : ""}`}
      style={{
        left: position.x,
        top: position.y,
        width: "150px",
        height: "100px",
        zIndex: isDragging ? 1000 : isHovered ? 100 : 10,
        cursor: isDragging ? "grabbing" : "grab",
        transform: isDragging ? "rotate(2deg) scale(1.05)" : "none",
        transition: "transform 0.2s ease",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Card
        className={`h-100 border-0 shadow-sm bg-success text-white position-relative`}
      >
        <Card.Body className="d-flex flex-column justify-content-center text-center p-2">
          <div className="fw-bold fs-5 mb-1">{number}</div>
          <div
            className="small fw-semibold mb-1"
            style={{ fontSize: "0.75rem" }}
          >
            {stopData?.name || `Stop ${number}`}
          </div>
          <div
            className="small opacity-75"
            style={{ fontSize: "0.65rem", fontFamily: "monospace" }}
          >
            {stopData?.code || `S${number.toString().padStart(3, "0")}`}
          </div>
        </Card.Body>

        {/* Delete Button */}
        <Button
          variant="danger"
          size="sm"
          className={`position-absolute rounded-circle p-0 ${
            isHovered ? "opacity-100" : "opacity-0"
          }`}
          style={{
            top: "-8px",
            right: "-8px",
            width: "24px",
            height: "24px",
            transition: "opacity 0.2s ease",
            zIndex: 5,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(id);
          }}
        >
          <FaTimes size={10} />
        </Button>

        {/* Connection Points */}
        {(isHovered || isConnecting) &&
          connectionPoints.map((point) => (
            <Button
              key={point.id}
              variant="primary"
              size="sm"
              className="position-absolute rounded-circle p-0 d-flex align-items-center justify-content-center"
              style={{
                ...point.style,
                width: "24px",
                height: "24px",
                zIndex: 6,
                transition: "all 0.2s ease",
              }}
              onClick={(e) => handleConnectionClick(e, point.id)}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <FaArrowRight size={8} className="text-white" />
            </Button>
          ))}
      </Card>
    </div>
  );
};

// Sidebar element component
const SidebarElement = ({ stopData, isUsed }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ELEMENT_TYPE,
    item: { number: stopData.id, fromSidebar: true, stopData },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !isUsed,
  });

  return (
    <Card
      ref={drag}
      className={`mb-2 border-0 shadow-sm ${
        isUsed ? "bg-secondary text-white opacity-75" : "bg-primary text-white"
      } ${isDragging ? "shadow-lg" : ""}`}
      style={{
        cursor: isUsed ? "not-allowed" : isDragging ? "grabbing" : "grab",
        transition: "all 0.2s ease",
        transform: isDragging ? "rotate(2deg) scale(1.05)" : "scale(1)",
      }}
    >
      <Card.Body className="p-3">
        <div className="d-flex align-items-center">
          <FaMapPin className="me-2" />
          <div className="flex-grow-1">
            <div className="fw-semibold mb-1">{stopData.name}</div>
            <div
              className="small opacity-75"
              style={{ fontFamily: "monospace" }}
            >
              {stopData.code}
            </div>
          </div>
          {isUsed && (
            <Badge bg="success" className="ms-2">
              Used
            </Badge>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

// Connection line component with enhanced delete functionality
const ConnectionLine = ({ from, to, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false);
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const connectionId = `connection-${Date.now()}-${Math.random()}`;

  return (
    <g 
      className="connection-line"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <defs>
        <marker
          id={`arrowhead-${connectionId}`}
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="#0d6efd" />
        </marker>
      </defs>

      <line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke={isHovered ? "#0d6efd" : "#0d6efd"}
        strokeWidth={isHovered ? "4" : "3"}
        markerEnd={`url(#arrowhead-${connectionId})`}
        className="connection-path"
        style={{ cursor: 'pointer', pointerEvents: 'none' }}
      />

      <circle
        cx={midX}
        cy={midY}
        r={isHovered ? "16" : "12"}
        fill="#dc3545"
        className="connection-delete"
        style={{ 
          cursor: 'pointer', 
          opacity: isHovered ? 1 : 0.8,
          transition: 'all 0.2s ease'
        }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      />

      <text
        x={midX}
        y={midY + 5}
        textAnchor="middle"
        fill="white"
        fontSize={isHovered ? "16" : "14"}
        fontWeight="bold"
        style={{ 
          cursor: 'pointer', 
          userSelect: 'none',
          pointerEvents: 'none'
        }}
      >
        ×
      </text>

      {/* Tooltip on hover */}
      {isHovered && (
        <g>
          <rect
            x={midX - 40}
            y={midY - 40}
            width="80"
            height="20"
            fill="rgba(0,0,0,0.8)"
            rx="4"
            style={{ pointerEvents: 'none' }}
          />
          <text
            x={midX}
            y={midY - 27}
            textAnchor="middle"
            fill="white"
            fontSize="11"
            style={{ pointerEvents: 'none' }}
          >
            Click to delete
          </text>
        </g>
      )}
    </g>
  );
};

// Route Order Panel Component
const RouteOrderPanel = ({ elements, connections }) => {
  const routeOrder = useMemo(() => {
    if (elements.length === 0) return [];
    
    const sortedElements = [...elements].sort((a, b) => a.position.y - b.position.y);
    
    return sortedElements.map((element, index) => {
      const stopData = ROUTE_STOPS.find(stop => stop.id === element.number);
      const hasIncomingConnection = connections.some(conn => conn.to === element.id);
      const hasOutgoingConnection = connections.some(conn => conn.from === element.id);
      
      return {
        ...element,
        sequenceNumber: index + 1,
        stopData,
        isStart: index === 0,
        isEnd: index === sortedElements.length - 1,
        hasIncomingConnection,
        hasOutgoingConnection,
        warning: index === 0 && hasIncomingConnection ? 'First stop should not have incoming connections' : null,
      };
    });
  }, [elements, connections]);

  return (
    <div className="h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
        <h6 className="mb-0 fw-bold">Route Sequence</h6>
        <Badge bg="info">{routeOrder.length} stops</Badge>
      </div>
      
      <div className="flex-grow-1 overflow-auto p-3" style={{ maxHeight: 'calc(115vh - 250px)' }}>
        {routeOrder.length === 0 ? (
          <div className="text-center py-5">
            <FaRoute size={48} className="text-muted mb-3" />
            <p className="text-muted mb-2">No stops added</p>
            <small className="text-muted">Drag stops to canvas</small>
          </div>
        ) : (
          <div>
            {routeOrder.map((stop, index) => (
              <Card key={stop.id} className="mb-3 border-0 shadow-sm">
                <Card.Body className="p-3">
                  <div className="d-flex align-items-start">
                    <div className="d-flex flex-column align-items-center me-3">
                      <Badge 
                        bg={stop.isStart ? 'success' : stop.isEnd ? 'danger' : 'primary'}
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px' }}
                      >
                        {stop.sequenceNumber}
                      </Badge>
                      {!stop.isEnd && (
                        <FaArrowDown className="text-muted mt-2" />
                      )}
                    </div>
                    
                    <div className="flex-grow-1">
                      <div className="fw-semibold mb-1">
                        {stop.stopData?.name || `Stop ${stop.number}`}
                      </div>
                      <div className="small text-muted mb-2" style={{ fontFamily: 'monospace' }}>
                        {stop.stopData?.code || `S${stop.number.toString().padStart(3, '0')}`}
                      </div>
                      
                      {stop.warning && (
                        <Alert variant="warning" className="p-2 mb-2 small">
                          {stop.warning}
                        </Alert>
                      )}
                      
                      <div className="d-flex flex-wrap gap-1">
                        {stop.isStart && <Badge bg="success" className="small">Start</Badge>}
                        {stop.isEnd && <Badge bg="danger" className="small">End</Badge>}
                        {!stop.hasIncomingConnection && !stop.isStart && (
                          <Badge bg="warning" className="small">No Input</Badge>
                        )}
                        {!stop.hasOutgoingConnection && !stop.isEnd && (
                          <Badge bg="warning" className="small">No Output</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {routeOrder.length > 0 && (
        <div className="p-3 border-top bg-light">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="small text-muted">Total Distance:</span>
            <span className="small fw-semibold">Calculating...</span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span className="small text-muted">Estimated Time:</span>
            <span className="small fw-semibold">~{routeOrder.length * 2} min</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Route Designer Component
const RouteDesignerContent = () => {
  const [elements, setElements] = useState([]);
  const [connections, setConnections] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isCanvasDragging, setIsCanvasDragging] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const [searchQuery, setSearchQuery] = useState("");

  const canvasRef = useRef(null);
  const usedNumbers = elements.map(el => el.number);

  // Filter route stops based on search
  const filteredRouteStops = useMemo(() => {
    return ROUTE_STOPS.filter(stop => 
      stop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      stop.code.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery]);

  // Drop zone for the main canvas
  const [{ isOver }, drop] = useDrop({
    accept: ELEMENT_TYPE,
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();
      const canvasRect = canvasRef.current.getBoundingClientRect();
      
      const x = (offset.x - canvasRect.left - pan.x) / zoom;
      const y = (offset.y - canvasRect.top - pan.y) / zoom;

      if (item.fromSidebar) {
        const newElement = {
          id: Date.now(),
          number: item.number,
          position: { x: Math.max(0, x - 75), y: Math.max(0, y - 50) },
          stopData: item.stopData,
        };
        setElements(prev => [...prev, newElement]);
      } else {
        setElements(prev => prev.map(el => 
          el.id === item.id 
            ? { ...el, position: { x: Math.max(0, x - 75), y: Math.max(0, y - 50) } }
            : el
        ));
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Delete element
  const deleteElement = (id) => {
    setElements(prev => prev.filter(el => el.id !== id));
    setConnections(prev => prev.filter(conn => conn.from !== id && conn.to !== id));
  };

  // Handle element connection
  const handleConnect = (elementId, connectionPoint) => {
    if (!isConnecting) {
      setIsConnecting(true);
      setSelectedElement({ id: elementId, point: connectionPoint });
    } else {
      if (selectedElement && selectedElement.id !== elementId) {
        const newConnection = {
          id: Date.now(),
          from: selectedElement.id,
          fromPoint: selectedElement.point,
          to: elementId,
          toPoint: connectionPoint,
        };
        setConnections(prev => [...prev, newConnection]);
      }
      setIsConnecting(false);
      setSelectedElement(null);
    }
  };

  // Delete connection
  const deleteConnection = (connectionId) => {
    setConnections(prev => prev.filter(conn => conn.id !== connectionId));
  };

  // Calculate connection positions
  const getConnectionPosition = (elementId, point) => {
    const element = elements.find(el => el.id === elementId);
    if (!element) return { x: 0, y: 0 };

    const baseX = element.position.x + 75;
    const baseY = element.position.y + 50;

    const offsets = {
      top: { x: 0, y: -50 },
      right: { x: 75, y: 0 },
      bottom: { x: 0, y: 50 },
      left: { x: -75, y: 0 },
    };

    const offset = offsets[point] || { x: 0, y: 0 };
    return {
      x: baseX + offset.x,
      y: baseY + offset.y,
    };
  };

  // Handle canvas mouse events
  const handleCanvasMouseDown = (e) => {
    if (
      e.target.classList.contains("canvas-area") ||
      e.target.classList.contains("canvas-content")
    ) {
      setIsCanvasDragging(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!isCanvasDragging) return;

    const deltaX = e.clientX - lastPanPoint.x;
    const deltaY = e.clientY - lastPanPoint.y;

    setPan((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));

    setLastPanPoint({ x: e.clientX, y: e.clientY });
  };

  const handleCanvasMouseUp = () => {
    setIsCanvasDragging(false);
  };

  const handleCanvasClick = (e) => {
    if (
      e.target.classList.contains("canvas-area") ||
      e.target.classList.contains("canvas-content")
    ) {
      setIsConnecting(false);
      setSelectedElement(null);
    }
  };

  // Zoom and control functions
  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));
  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };
  const clearAll = () => {
    setElements([]);
    setConnections([]);
    setIsConnecting(false);
    setSelectedElement(null);
  };

  return (
    <div className="vh-100 bg-light">
      <Row className="h-100 g-0">
        {/* Left Sidebar - Available Stops */}
        <Col lg={3} md={4} sm={12} className="h-100">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-light text-white py-3">
              <h6 className="mb-0 fw-bold">Available Stops</h6>
            </Card.Header>
            <Card.Body className="p-0 d-flex flex-column h-100">
              <div className="p-3 border-bottom">
                <InputGroup size="sm">
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search stops..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </div>

              <div
                className="flex-grow-1 overflow-auto p-3"
                style={{ maxHeight: "calc(110vh - 200px)" }}
              >
                {filteredRouteStops.map((stop) => (
                  <SidebarElement
                    key={stop.id}
                    stopData={stop}
                    isUsed={usedNumbers.includes(stop.id)}
                  />
                ))}

                {filteredRouteStops.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">No stops found</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Main Canvas */}
        <Col lg={6} md={8} sm={12} className="h-100">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-light py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold">Route Designer</h6>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleZoomOut}
                  >
                    <FaSearchMinus />
                  </Button>
                  <span className="small text-muted px-2">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleZoomIn}
                  >
                    <FaSearchPlus />
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={resetView}
                  >
                    <FaRedo />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={clearAll}>
                    <FaTrashAlt />
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0 position-relative">
              <div
                ref={(node) => {
                  canvasRef.current = node;
                  drop(node);
                }}
                className={`canvas-area w-100 h-100 position-relative overflow-hidden ${
                  isOver
                    ? "bg-success bg-opacity-10 border border-success border-2"
                    : ""
                } ${isCanvasDragging ? "cursor-grabbing" : "cursor-grab"}`}
                style={{
                  minHeight: "500px",
                  backgroundImage: `radial-gradient(circle at 20px 20px, rgba(13, 110, 253, 0.1) 1px, transparent 1px),
                                   radial-gradient(circle at 80px 80px, rgba(13, 110, 253, 0.05) 1px, transparent 1px)`,
                  backgroundSize: "40px 40px, 80px 80px",
                }}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                onClick={handleCanvasClick}
              >
                <div
                  className="canvas-content position-relative w-100 h-100"
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: "0 0",
                    minHeight: "500px",
                    minWidth: "600px",
                  }}
                >
                  {/* SVG for connections */}
                  <svg
                    className="position-absolute top-0 start-0 w-100 h-100"
                    style={{ zIndex: 1 }}
                  >
                    {connections.map((conn) => {
                      const fromPos = getConnectionPosition(
                        conn.from,
                        conn.fromPoint
                      );
                      const toPos = getConnectionPosition(
                        conn.to,
                        conn.toPoint
                      );
                      return (
                        <ConnectionLine
                          key={conn.id}
                          from={fromPos}
                          to={toPos}
                          onDelete={() => deleteConnection(conn.id)}
                        />
                      );
                    })}
                  </svg>

                  {/* Route Elements */}
                  {elements.map((element) => {
                    const stopData = ROUTE_STOPS.find(
                      (stop) => stop.id === element.number
                    );
                    return (
                      <RouteElement
                        key={element.id}
                        id={element.id}
                        number={element.number}
                        position={element.position}
                        onDelete={deleteElement}
                        onConnect={handleConnect}
                        isConnecting={isConnecting}
                        stopData={stopData}
                      />
                    );
                  })}

                  {/* Empty state */}
                  {elements.length === 0 && (
                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                      <FaRoute size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">Design Your Route</h5>
                      <p className="text-muted">
                        Drag stops from the left panel to create your route
                      </p>
                      <div className="mt-3">
                        <Badge bg="primary" className="me-2">
                          1
                        </Badge>
                        <small className="text-muted me-3">
                          Drag stops to canvas
                        </small>
                        <Badge bg="primary" className="me-2">
                          2
                        </Badge>
                        <small className="text-muted me-3">
                          Hover to see connection points
                        </small>
                        <Badge bg="primary" className="me-2">
                          3
                        </Badge>
                        <small className="text-muted">
                          Click arrows to connect stops
                        </small>
                        <Badge bg="danger" className="me-2">
                          4
                        </Badge>
                        <small className="text-muted">
                          Hover connections to delete them
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Connection mode indicator */}
              {isConnecting && (
                <div
                  className="position-absolute top-0 start-50 translate-middle-x mt-3"
                  style={{ zIndex: 50 }}
                >
                  <Alert variant="info" className="mb-0 py-2 px-3 small">
                    <strong>Connection Mode</strong> - Click another stop to
                    connect
                  </Alert>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right Panel - Route Order */}
        <Col lg={3} className="h-100 d-none d-lg-block">
          <Card className="h-100 border-0 shadow-sm">
            {/* <Card.Header className="bg-success text-white py-3">
              <h6 className="mb-0 fw-bold">Route Order</h6>
            </Card.Header> */}
            <Card.Body className="p-0">
              <RouteOrderPanel elements={elements} connections={connections} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Main wrapper component with DndProvider
const TransportRouteDesigner = () => {
  return (
    <DndProvider backend={HTML5Backend}>
      <RouteDesignerContent />
    </DndProvider>
  );
};

export default TransportRouteDesigner;